import React, { useState } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import {
    useTheme, Box, Typography,
    Table, TableBody, TableCell,
    TableContainer, TableHead,
    TableRow, Paper, Collapse,
} from "@mui/material";
import { LAYOUT_HEIGHT } from "state/constants";


const RouteTableau = ({ data, isAverage }) => {
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const [openCell, setOpenCell] = useState(null);
    const [blinkCell, setBlinkCell] = useState(null);

    const handleClick = (cellKey) => {
        setOpenCell(openCell === cellKey ? null : cellKey);
        setBlinkCell(cellKey);
        setTimeout(() => setBlinkCell(null), 600);
    };

    if (!data || data.length === 0) {
        return (
            <Box p={1} sx={{ height: '100%', width: '100%' }}>
                <Typography>Nothing to display</Typography>
            </Box>
        );
    }

    const parsed = data.map((d) => {
        const [field1, field2] = d.key.split("|");
        return {
            field1,
            field2,
            totalBH: d.totalBH ?? 0,
            rawBH: d.rawBH ?? d.totalBH ?? 0,
            totalDays: d.totalDays ?? 0,
            flights: d.flights || [],
        };
    });

    const avgBH = (items) => {
        const days = items.reduce((s, v) => s + (v?.totalDays ?? 0), 0);
        const raw = items.reduce((s, v) => s + (v?.rawBH ?? v?.totalBH ?? 0), 0);
        return days > 0 ? parseFloat((raw / days).toFixed(2)) : parseFloat(raw.toFixed(1));
    };

    const isValidDateString = (v) =>
        typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v) && !isNaN(Date.parse(v));

    const field1Values = [...new Set(parsed.map((p) => p.field1))].sort((a, b) => {
        if (isValidDateString(a) && isValidDateString(b)) {
            return new Date(a) - new Date(b);
        }
        return a.localeCompare(b);
    });

    const field2Values = [...new Set(parsed.map((p) => p.field2))].sort();

    const fmt = (v) => (v > 0 ? v.toFixed(1) : "-");

    const isLocal = timeMode !== "UTC";

    return (
        <TableContainer component={Paper} sx={{ maxHeight: LAYOUT_HEIGHT }}>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: "bold", minWidth: "90px" }}>
                            Field
                        </TableCell>
                        {field1Values.map((field1) => (
                            <TableCell key={field1} align="center" sx={{ fontWeight: "bold" }}>
                                {isValidDateString(field1) ? format(new Date(field1), "dd.MM.yy") : field1}
                            </TableCell>
                        ))}
                        <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            {isAverage ? "Average" : "Total"}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {field2Values.map((field2) => {
                        const rowValues = field1Values.map((field1) =>
                            parsed.find((p) => p.field1 === field1 && p.field2 === field2)
                        );
                        const rowTotal = avgBH(rowValues.filter(Boolean));

                        return (
                            <React.Fragment key={field2}>
                                <TableRow>
                                    <TableCell component="th" scope="row">
                                        {field2 || ""}
                                    </TableCell>
                                    {field1Values.map((field1) => {
                                        const found = parsed.find(
                                            (p) => p.field1 === field1 && p.field2 === field2
                                        );
                                        const cellKey = `${field1}-${field2}`;
                                        return (
                                            <TableCell
                                                key={cellKey}
                                                align="center"
                                                onClick={() => handleClick(cellKey)}
                                                sx={{
                                                    cursor: "pointer",
                                                    backgroundColor:
                                                        openCell === cellKey
                                                            ? theme.palette.action.selected
                                                            : "inherit",
                                                    fontWeight: openCell === cellKey ? "bold" : "normal",
                                                    ...(blinkCell === cellKey && {
                                                        animation: "blink 0.6s ease-in-out",
                                                    }),
                                                    "@keyframes blink": {
                                                        "0%": {
                                                            backgroundColor: theme.palette.primary.light,
                                                            color: theme.palette.text.primary,
                                                        },
                                                        "50%": {
                                                            backgroundColor: theme.palette.action.hover,
                                                        },
                                                        "100%": { backgroundColor: "inherit" },
                                                    },
                                                }}
                                            >
                                                {found ? fmt(found.totalBH) : "-"}
                                            </TableCell>
                                        );
                                    })}
                                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                        {fmt(rowTotal)}
                                    </TableCell>
                                </TableRow>

                                {/* Detail rows */}
                                {field1Values.map((field1) => {
                                    const found = parsed.find(
                                        (p) => p.field1 === field1 && p.field2 === field2
                                    );
                                    const cellKey = `${field1}-${field2}`;

                                    return (
                                        openCell === cellKey &&
                                        found && found.flights.length > 0 && (
                                            <TableRow key={`${cellKey}-details`}>
                                                <TableCell colSpan={field1Values.length + 2} sx={{ p: 0 }}>
                                                    <Collapse
                                                        in={openCell === cellKey}
                                                        timeout="auto"
                                                        unmountOnExit
                                                    >
                                                        <Box marginLeft={2}>
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow
                                                                        sx={{ "& .MuiTableCell-root": { color: theme.palette.primary.dark } }}
                                                                    >
                                                                        <TableCell>Date</TableCell>
                                                                        <TableCell>FlightNo</TableCell>
                                                                        <TableCell>CrewRoute</TableCell>
                                                                        <TableCell>ACType</TableCell>
                                                                        <TableCell>Reg</TableCell>
                                                                        <TableCell>BH</TableCell>
                                                                        <TableCell>Sectors</TableCell>
                                                                        <TableCell>Days</TableCell>
                                                                        {/* <TableCell>CrewCount</TableCell> */}
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {found.flights.map((r, idx) => {
                                                                        const routeDate = isLocal
                                                                            ? r.routeDateLocalBase
                                                                            : r.routeDate;
                                                                        return (
                                                                            <TableRow key={idx}>
                                                                                <TableCell>
                                                                                    {routeDate
                                                                                        ? format(new Date(routeDate), "dd.MM.yy")
                                                                                        : "-"}
                                                                                </TableCell>
                                                                                <TableCell>{r.flightNo ?? "-"}</TableCell>
                                                                                <TableCell>{r.crewRoute ?? "-"}</TableCell>
                                                                                <TableCell>{r.acType ?? "-"}</TableCell>
                                                                                <TableCell>{r.flightReg ?? "-"}</TableCell>
                                                                                <TableCell>{r.blockHours?.toFixed(2) ?? "-"}</TableCell>
                                                                                <TableCell>{r.sectors ?? "-"}</TableCell>
                                                                                <TableCell>{r.routeDays ?? "-"}</TableCell>
                                                                                {/* <TableCell>{r.crewCount ?? "-"}</TableCell> */}
                                                                            </TableRow>
                                                                        );
                                                                    })}
                                                                </TableBody>
                                                            </Table>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
                {/* Total row */}
                {(() => {
                    const grandTotal = avgBH(parsed);
                    return (
                        <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>
                                {isAverage ? "Average" : "Total"}
                            </TableCell>
                            {field1Values.map((field1) => {
                                const colTotal = avgBH(parsed.filter((p) => p.field1 === field1));
                                return (
                                    <TableCell
                                        key={`total-${field1}`}
                                        align="center"
                                        sx={{ fontWeight: "bold" }}
                                    >
                                        {fmt(colTotal)}
                                    </TableCell>
                                );
                            })}
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                {fmt(grandTotal)}
                            </TableCell>
                        </TableRow>
                    );
                })()}
            </Table>
        </TableContainer>
    );
};

export default RouteTableau;
